import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { shouldUseDryRun } from "@/app/_module/utils/environment-config";
import { showToast } from "@/app/_module/utils/show-toast";
import { transformErrorResponse } from "@/app/_module/utils/transform-error-response";

import type { SendFaxRequest, FaxType } from "../types/types";

// Interface declarations (moved to follow ESLint rule)
interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
  timestamp: string;
  timeTakenMs: number;
  path: string;
}

export const FaxServices = {
  sendFax: async (
    data: SendFaxRequest
  ) => {
    try {
      const formData = new FormData();

      formData.append("toNumber", data.toNumber);
      if (data.fromNumber) {
        formData.append("fromNumber", data.fromNumber);
      }
      if (data.coverPage) {
        formData.append("coverPage", JSON.stringify(data.coverPage));
      }
      if (data.priority) {
        formData.append("priority", data.priority);
      }
      if (data.scheduledTime) {
        formData.append("scheduledTime", data.scheduledTime);
      }
      if (data.metadata) {
        formData.append("metadata", JSON.stringify(data.metadata));
      }

      if (Array.isArray(data.documents) && data.documents.length > 0) {
        // Send documents as JSON since backend expects DocumentDto[]
        formData.append("documents", JSON.stringify(data.documents));
      } else {
        throw new Error("At least one document is required");
      }

      const response = await handleApiCall<FaxType>(async () => await api.post<ApiResponse<FaxType>>({
        url: shouldUseDryRun() ? getApiUrl(endpoint.fax.sendFax) + "?dryRun=true" : getApiUrl(endpoint.fax.sendFax),
        body: formData,
        auth: true, // Preserve your Supabase JWT authentication
        options: {
          headers: {},
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
        },
      }));

      // handleApiCall already unwraps the data, so response IS the FaxType object
      if (response && response.faxId) {
        showToast("Fax Sent Successfully");
        return response;
      } else {
        throw new Error("Invalid fax response: missing faxId");
      }
    } catch (err) {
      // Following user memory: show user-friendly error messages
      const errorMessage = transformErrorResponse(err)?.message || "Failed to send fax";
      showToast(errorMessage, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(err);
    }
  },

  getFaxHistory: async (params?: { limit?: number; offset?: number; status?: 'pending' | 'sent' | 'delivered' | 'failed'; direction?: 'inbound' | 'outbound' }) => {
    try {
      const qs = new URLSearchParams();
      if (params?.limit != null) qs.append("limit", String(params.limit));
      if (params?.offset != null) qs.append("offset", String(params.offset));
      if (params?.status) qs.append("status", params.status);
      if (params?.direction) qs.append("direction", params.direction);
      if (shouldUseDryRun()) qs.append("dryRun", "true");

      const url =
        qs.toString().length > 0
          ? `${getApiUrl(endpoint.fax.getFaxHistory)}?${qs.toString()}`
          : getApiUrl(endpoint.fax.getFaxHistory);

      // handleApiCall already unwraps the data from the response
      const response = await handleApiCall(async () => await api.get({
        url,
        auth: true, // Preserve your Supabase JWT authentication
      }));
      // Response is already the unwrapped data: { items: [], meta: { page, totalItems, totalPages, itemCount } }
      return response;
    } catch (error) {
      // Following user memory: return empty array instead of throwing for better UX

      return { items: [], meta: { page: 1, totalItems: 0, totalPages: 1, itemCount: 0 } };
    }
  },

  getDailyUsage: async (): Promise<{ sent: number; limit: number; remaining: number }> => {
    try {
      const response = await handleApiCall<{ sent: number; limit: number; remaining: number }>(
        async () => await api.get({ url: getApiUrl(endpoint.fax.getDailyUsage), auth: true })
      );
      return response;
    } catch {
      return { sent: 0, limit: 100, remaining: 100 };
    }
  },
};
